import React, { useState } from 'react';
import { styled, Box, Typography, useTheme, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Fund } from '../../api/useFunds';
import { FundInputEditMobile } from '../FundInput/FundInputEditMobile';
import { FundAutocomplete } from '../FundAutocompleteSection/FundAutocomplete';

const InnerModalContainer = styled(Box)(({ theme }) => ({
  borderRadius: '12px 12px 0 0',
  boxShadow: '0px 4px 12px -1px rgba(30, 39, 50, 0.12), 0px 4px 16px -2px rgba(30, 39, 50, 0.16)',
  backgroundColor: 'white',
  padding: theme.spacing(4),
  height: '100%',
}));

const ApplyButton = styled(Button)(({ theme }) => ({
  fontSize: '16px',
  lineHeight: '24px',
  fontWeight: 500,
  textTransform: 'none',
  borderRadius: '6px',
  padding: theme.spacing(2.5, 4),
}));

const CancelButton = styled(Button)(({ theme }) => ({
  borderColor: 'transparent',
  color: theme.palette.text.primary,
  boxShadow: 'none !important',
  fontSize: '16px',
  lineHeight: '24px',
  fontWeight: 500,
  textTransform: 'none',
  borderRadius: '6px',
  padding: theme.spacing(2.5, 4),
}));

type FundEditModalProps = {
  fund: Fund | null;
  bic?: Bic;
  investedAmount?: number;
  onApply: (fund: Fund, amount: number) => void;
  onCancel: () => void;
};

export const FundEditModal = ({
  fund,
  bic,
  investedAmount,
  onApply,
  onCancel,
}: FundEditModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [isAmountDisabled, setIsAmountDisabled] = useState(!fund);
  const [amount, setAmount] = useState(investedAmount || 10000);
  const [selectedFund, setSelectedFund] = useState(fund);

  return (
    <InnerModalContainer className="fund-modal-inner-container">
      <Typography
        variant="body1"
        sx={{ marginBottom: theme.spacing(5) }}
        data-testid="fund-edit-modal-header"
      >
        {fund
          ? t('cashbackCalculator.detailed.fundModal.labelEdit')
          : t('cashbackCalculator.detailed.fundModal.labelAdd')}
      </Typography>
      <Box sx={{ marginBottom: theme.spacing(4) }}>
        <FundAutocomplete
          fullWidth
          disabled={fund !== null}
          id="fund-autocomplete-modal"
          mode="simple"
          onChange={(fund) => {
            setSelectedFund(fund);
            setIsAmountDisabled(false);
          }}
          selectedFund={fund}
        />
      </Box>
      <FundInputEditMobile
        fund={selectedFund}
        onChange={(fund, value) => {
          setAmount(value);
        }}
        bic={bic}
        testId="fund-input-mobile"
        investedAmount={amount}
        isAmountDisabled={isAmountDisabled}
      />
      <Box display="flex" flexDirection="column" justifyContent="center" gap={3}>
        <ApplyButton
          variant="contained"
          data-testid="add-fund-button"
          disabled={!selectedFund}
          onClick={() => selectedFund && onApply(selectedFund, amount)}
          color="primary"
        >
          {t('cashbackCalculator.detailed.fundModal.apply')}
        </ApplyButton>
        <CancelButton variant="outlined" onClick={onCancel} data-testid="cancel-button">
          {t('cashbackCalculator.detailed.fundModal.cancel')}
        </CancelButton>
      </Box>
    </InnerModalContainer>
  );
};
